import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chapter, Video, Task } from '../../../types';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { Badge } from '../../../components/ui/badge';
import { PlayCircle, Lock, CheckCircle, FileText, HelpCircle, CheckSquare } from 'lucide-react';
import { cn } from '../../../lib/utils'; // Assuming this exists or I will use simple str concat

interface CourseCurriculumProps {
    chapters: Chapter[];
    selectedVideo: Video | null;
    selectedChapter: Chapter | null;
    chapterVideosMap: Record<number, Video[]>;
    videoTasksMap: Record<number, Task[]>;
    chapterTasksCountMap: Record<number, number>;
    onVideoSelect: (video: Video) => void;
    isChapterUnlocked: (chapter: Chapter, index: number) => boolean;
    isVideoUnlocked: (video: Video) => boolean;
    isVideoCompleted: (videoId: number) => boolean;
}

export const CourseCurriculum: React.FC<CourseCurriculumProps> = ({
    chapters,
    selectedVideo,
    selectedChapter,
    chapterVideosMap,
    videoTasksMap,
    chapterTasksCountMap,
    onVideoSelect,
    isChapterUnlocked,
    isVideoUnlocked,
    isVideoCompleted
}) => {
    const { t } = useTranslation();

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-50 bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-900">{t('course.tabs.curriculum')}</h2>
                <p className="text-sm text-gray-500 mt-1">
                    {chapters.length} chapters • {Object.values(chapterVideosMap).flat().length} {t('course.sidebar.videos').toLowerCase()}
                </p>
            </div>

            <Accordion
                type="single"
                collapsible
                defaultValue={selectedChapter ? `item-${selectedChapter.id}` : undefined}
                className="w-full"
            >
                {chapters.map((chapter, cIndex) => {
                    const isUnlocked = isChapterUnlocked(chapter, cIndex);
                    const tasksCount = chapterTasksCountMap[chapter.id] || 0;
                    const videos = chapterVideosMap[chapter.id] || [];

                    return (
                        <AccordionItem key={chapter.id} value={`item-${chapter.id}`} className="border-b last:border-0 border-gray-50">
                            <AccordionTrigger className="px-6 py-4 hover:bg-gray-50 hover:no-underline transition-colors">
                                <div className="flex flex-col items-start gap-1 text-left w-full">
                                    <span className="text-sm font-medium text-indigo-600">
                                        {t('course.curriculum.chapter', {
                                            index: cIndex + 1,
                                            title: chapter.title,
                                            interpolation: { escapeValue: false }
                                        })}
                                    </span>
                                    <div className="flex items-center gap-2 text-xs text-gray-400">
                                        <span>{videos.length} videos</span>
                                        {tasksCount > 0 && <span>• {tasksCount} tasks</span>}
                                        {!isUnlocked && (
                                            <Badge variant="secondary" className="bg-gray-100 text-gray-500 h-5 px-1.5 font-normal">
                                                <Lock className="w-3 h-3 mr-1" /> {t('course.curriculum.locked')}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="bg-gray-50/30">
                                {videos.map((video, vIndex) => {
                                    const videoUnlocked = isUnlocked && isVideoUnlocked(video);
                                    const isSelected = selectedVideo?.id === video.id;
                                    const completed = isVideoCompleted(video.id);
                                    const duration = video.duration
                                        ? t('course.curriculum.duration', {
                                            minutes: Math.floor(video.duration / 60),
                                            seconds: Math.floor(video.duration % 60)
                                        })
                                        : '';
                                    const tasks = videoTasksMap[video.id] || [];

                                    return (
                                        <div
                                            key={video.id}
                                            className={cn(
                                                "flex flex-col border-l-2 ml-6 pl-4 py-3 cursor-pointer transition-colors relative",
                                                isSelected ? "border-indigo-600 bg-indigo-50/50" : "border-transparent hover:bg-gray-100/50",
                                                !videoUnlocked && "opacity-60 pointer-events-none"
                                            )}
                                            onClick={() => videoUnlocked && onVideoSelect(video)}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex items-start gap-3">
                                                    {completed ? (
                                                        <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                                                    ) : videoUnlocked ? (
                                                        <PlayCircle className={cn(
                                                            "w-5 h-5 shrink-0 mt-0.5",
                                                            isSelected ? "text-indigo-600" : "text-gray-400"
                                                        )} />
                                                    ) : (
                                                        <Lock className="w-5 h-5 text-gray-300 shrink-0 mt-0.5" />
                                                    )}

                                                    <div className="flex flex-col gap-0.5">
                                                        <div className={cn(
                                                            "text-sm font-medium leading-tight",
                                                            isSelected ? "text-indigo-900" : "text-gray-700",
                                                            completed && "text-gray-500 line-through decoration-gray-300"
                                                        )}>
                                                            {video.title}
                                                        </div>
                                                        {duration && <span className="text-xs text-gray-400">{duration}</span>}
                                                    </div>
                                                </div>

                                                {video.is_free && !isUnlocked && (
                                                    <Badge variant="outline" className="text-xs border-green-200 text-green-600 bg-green-50">
                                                        {t('course.curriculum.preview')}
                                                    </Badge>
                                                )}
                                            </div>

                                            {/* Tasks Indicator */}
                                            {tasks.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2 ml-8">
                                                    {tasks.map(task => {
                                                        let Icon = FileText;
                                                        if (task.task_type === 'multiple_choice') Icon = HelpCircle;
                                                        if (task.task_type === 'form') Icon = CheckSquare;

                                                        const isTaskCompleted = (task.task_type === 'upload' || task.task_type === 'form')
                                                            ? task.submission_status === 'approved'
                                                            : task.completed;

                                                        return (
                                                            <div
                                                                key={task.id}
                                                                className={cn(
                                                                    "flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border",
                                                                    isTaskCompleted
                                                                        ? "bg-green-50 text-green-700 border-green-200"
                                                                        : "bg-white text-gray-500 border-gray-200"
                                                                )}
                                                                title={task.title}
                                                            >
                                                                <Icon className="w-3 h-3" />
                                                                <span className="max-w-[100px] truncate">{task.title}</span>
                                                                {isTaskCompleted && <CheckCircle className="w-3 h-3 ml-1 fill-current" />}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </AccordionContent>
                        </AccordionItem>
                    );
                })}
            </Accordion>
        </div>
    );
};
